import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import type { MakeupBookingAdminRow } from "@/types/makeupTypes";
import { v4 as uuidv4 } from 'uuid';

export interface MakeupClass {
  id: string;
  class_id: string;
  date: string;
  start_time: string;
  end_time: string;
  max_students: number;
  current_students: number;
  created_at?: string;
  updated_at?: string;
  
  // Join data
  class?: {
    name: string;
    level?: string;
    location?: {
      name: string;
    };
    instructor?: {
      first_name: string;
      last_name: string;
    };
  };
}

export interface MakeupBooking {
  id: string;
  student_id: string;
  absence_id: string;
  makeup_class_id: string;
  notes?: string;
  status: 'scheduled' | 'completed' | 'cancelled';
  created_at?: string;
  updated_at?: string;
}

export const MakeupService = {
  /**
   * Helper to check if class join is valid
   */
  isValidClassJoin(cls: any): cls is { name: string } {
    return cls && typeof cls === 'object' && 'name' in cls;
  },

  /**
   * Get available makeup classes for a specific class type
   */
  getAvailableMakeupClasses: async (classId: string): Promise<MakeupClass[]> => {
    try {
      const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      // Supabase JS client does NOT support column-to-column comparisons (e.g., current_students < max_students)
      // So, we fetch all future classes for this classId and filter in JS below.
      const { data, error } = await supabase
        .from("makeup_classes")
        .select(`
          *,
          class:class_id (
            name,
            level,
            location:location_id (
              name
            ),
            class_staff_assignments:class_staff_assignments(
              staff_member:staff_id(
                id,
                first_name,
                last_name
              )
            )
          )
        `)
        .eq("class_id", classId)
        .gte("date", today)
        .order("date", { ascending: true });

      if (error) {
        console.error("Error fetching makeup classes:", error);
        throw error;
      }

      // Filter for available classes in JS because Supabase JS does not support column-to-column comparison
      // Also filter out any records where the class join failed (class is not a valid object)
      // This is safe and effective for small/medium datasets
      const availableClasses = (data || [])
        .filter(mc => mc.current_students < mc.max_students)
        .filter(mc => MakeupService.isValidClassJoin(mc.class)) as unknown as MakeupClass[];
      // The cast is safe because we have filtered for valid class joins
      return availableClasses;
    } catch (error) {
      console.error("Error in getAvailableMakeupClasses:", error);
      toast.error("Failed to load available makeup classes");
      return [];
    }
  },

  /**
   * Get all available makeup classes
   */
  getAllAvailableMakeupClasses: async (): Promise<MakeupClass[]> => {
    try {
      const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      
      // Supabase JS client does NOT support column-to-column comparisons (e.g., current_students < max_students)
      // So, we fetch all future classes and filter in JS below.
      const { data, error } = await supabase
        .from("makeup_classes")
        .select(`
          *,
          class:class_id (
            name,
            level,
            location:location_id (
              name
            ),
            class_staff_assignments:class_staff_assignments(
              staff_member:staff_id(
                id,
                first_name,
                last_name
              )
            )
          )
        `)
        .gte("date", today)
        .order("date", { ascending: true });

      if (error) {
        console.error("Error fetching all makeup classes:", error);
        throw error;
      }

      // Filter for available classes in JS because Supabase JS does not support column-to-column comparison
      // Also filter out any records where the class join failed (class is not a valid object)
      // This is safe and effective for small/medium datasets
      const availableClasses = (data || [])
        .filter(mc => mc.current_students < mc.max_students)
        .filter(mc => MakeupService.isValidClassJoin(mc.class)) as unknown as MakeupClass[];
      // The cast is safe because we have filtered for valid class joins
      return availableClasses;
    } catch (error) {
      console.error("Error in getAllAvailableMakeupClasses:", error);
      toast.error("Failed to load available makeup classes");
      return [];
    }
  },

  /**
   * Schedule a makeup class for a student
   */
  scheduleMakeup: async (
    studentId: string,
    absenceId: string,
    makeupClassId: string,
    notes?: string
  ): Promise<MakeupBooking | null> => {
    try {
      // First, check if the makeup class has space available
      const { data: makeupClass, error: makeupClassError } = await supabase
        .from("makeup_classes")
        .select(`
          current_students, max_students, class:class_id (
            class_staff_assignments:class_staff_assignments(
              staff_member:staff_id(
                id,
                first_name,
                last_name
              )
            )
          )
        `)
        .eq("id", makeupClassId)
        .single();

      if (makeupClassError) {
        console.error("Error fetching makeup class:", makeupClassError);
        toast.error("Failed to check makeup class availability");
        throw makeupClassError;
      }

      if (makeupClass.current_students >= makeupClass.max_students) {
        toast.error("This makeup class is now full");
        return null;
      }

      // Check if absence already has a makeup scheduled
      const { data: existingBooking, error: existingBookingError } = await supabase
        .from("makeup_bookings")
        .select("id, status")
        .eq("absence_id", absenceId)
        .maybeSingle();

      if (existingBookingError) {
        console.error("Error checking existing bookings:", existingBookingError);
        toast.error("Failed to check existing makeup bookings");
        throw existingBookingError;
      }

      if (existingBooking && existingBooking.status === "scheduled") {
        toast.error("This absence already has a scheduled makeup");
        return null;
      }

      // Get orgId from current user
      const { data: userData } = await supabase.auth.getUser();
      const orgId = userData?.user?.user_metadata?.organization_id;

      // If there was a cancelled booking, update it instead of creating a new one
      if (existingBooking && existingBooking.status === "cancelled") {
        const { data: updatedBooking, error: updateError } = await supabase
          .from("makeup_bookings")
          .update({
            makeup_class_id: makeupClassId,
            notes: notes,
            status: "scheduled",
            updated_at: new Date().toISOString(),
            organization_id: orgId // ensure orgId is set on update too
          })
          .eq("id", existingBooking.id)
          .select()
          .single();

        if (updateError) {
          console.error("Error updating makeup booking:", updateError);
          toast.error("Failed to reschedule makeup");
          throw updateError;
        }

        toast.success("Makeup class rescheduled");
        return updatedBooking as MakeupBooking;
      }

      // Create a new booking
      const { data: newBooking, error: insertError } = await supabase
        .from("makeup_bookings")
        .insert({
          student_id: studentId,
          absence_id: absenceId,
          makeup_class_id: makeupClassId,
          notes: notes,
          status: "scheduled",
          organization_id: orgId // always set orgId
        })
        .select()
        .single();

      if (insertError) {
        console.error("Error scheduling makeup:", insertError);
        toast.error("Failed to schedule makeup");
        throw insertError;
      }

      // Insert into activity_feed
      try {
        const { data } = await supabase.auth.getUser();
        const user = data?.user;
        const orgId = user && 'user_metadata' in user ? (user as any).user_metadata?.organization_id : undefined;
        const userId = user?.id;
        await supabase.from("activity_feed").insert({
          organization_id: orgId,
          user_id: userId,
          type: "makeup_booking",
          message: `Scheduled makeup class for student`,
          data: {
            student_id: studentId,
            absence_id: absenceId,
            makeup_class_id: makeupClassId,
            notes,
            status: "scheduled"
          }
        });
      } catch (err) {
        // Log but don't block
        console.warn("Failed to log activity_feed for makeup booking", err);
      }

      toast.success("Makeup class scheduled successfully");
      return newBooking as MakeupBooking;
    } catch (error) {
      console.error("Error in scheduleMakeup:", error);
      toast.error("Failed to schedule makeup class");
      return null;
    }
  },

  /**
   * Cancel a scheduled makeup
   */
  cancelMakeup: async (bookingId: string): Promise<boolean> => {
    try {
      const { data: updated, error } = await supabase
        .from("makeup_bookings")
        .update({
          status: "cancelled",
          updated_at: new Date().toISOString()
        })
        .eq("id", bookingId)
        .select()
        .single();

      if (error) {
        console.error("Error cancelling makeup:", error);
        toast.error("Failed to cancel makeup");
        throw error;
      }

      // Insert into activity_feed
      try {
        const { data } = await supabase.auth.getUser();
        const user = data?.user;
        const orgId2 = user && 'user_metadata' in user ? (user as any).user_metadata?.organization_id : undefined;
        const userId2 = user?.id;
        await supabase.from("activity_feed").insert({
          organization_id: orgId2,
          user_id: userId2,
          type: "makeup_booking",
          message: `Cancelled makeup class booking`,
          data: {
            booking_id: bookingId,
            status: "cancelled"
          }
        });
      } catch (err) {
        // Log but don't block
        console.warn("Failed to log activity_feed for makeup cancellation", err);
      }

      toast.success("Makeup class cancelled successfully");
      return true;
    } catch (error) {
      console.error("Error in cancelMakeup:", error);
      toast.error("Failed to cancel makeup class");
      return false;
    }
  },

  /**
   * Get all makeup bookings for a student
   */
  getStudentMakeupBookings: async (studentId: string): Promise<unknown[]> => {
    try {
      const { data, error } = await supabase
        .from("makeup_bookings")
        .select(`
          *,
          makeup_class:makeup_class_id (
            date,
            start_time,
            end_time,
            class:class_id (
              name,
              level,
              location (
                name
              ),
              class_staff_assignments:class_staff_assignments(
                staff_member:staff_id(
                  id,
                  first_name,
                  last_name
                )
              )
            )
          ),
          absence:absence_id (
            date,
            class:class_id (
              name
            )
          )
        `)
        .eq("student_id", studentId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching student makeups:", error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error("Error in getStudentMakeupBookings:", error);
      toast.error("Failed to load makeup bookings");
      return [];
    }
  },

  /**
   * Mark an absence as eligible for a makeup class
   */
  markAbsenceForMakeup: async (absenceId: string, isEligible: boolean): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("absences")
        .update({
          is_makeup_available: isEligible,
          updated_at: new Date().toISOString()
        })
        .eq("id", absenceId);

      if (error) {
        console.error("Error updating absence makeup eligibility:", error);
        toast.error("Failed to update makeup eligibility");
        throw error;
      }

      toast.success(`Absence ${isEligible ? 'marked' : 'unmarked'} for makeup`);
      return true;
    } catch (error) {
      console.error("Error in markAbsenceForMakeup:", error);
      toast.error("Failed to update makeup eligibility");
      return false;
    }
  },

  /**
   * Create a new makeup class option
   */
  createMakeupClass: async (makeupClass: any): Promise<MakeupClass | null> => {
    try {
      const { data, error } = await supabase
        .from("makeup_classes")
        .insert(makeupClass)
        .select()
        .single();

      if (error) {
        console.error("Error creating makeup class:", error);
        toast.error("Failed to create makeup class");
        throw error;
      }

      toast.success("Makeup class created successfully");
      return data;
    } catch (error) {
      console.error("Error in createMakeupClass:", error);
      toast.error("Failed to create makeup class");
      return null;
    }
  },

  /**
   * Get all makeup bookings for the current organization (admin view)
   */
  getAllMakeupBookings: async (organizationId: string): Promise<MakeupBookingAdminRow[]> => {
    try {
      // NOTE: TypeScript cannot infer the type for this deep select, so we cast the select() call to any to break the inference chain.
      const selectString = `
        *,
        student:student_id (first_name, last_name),
        makeup_class:makeup_class_id (
          date, start_time, end_time, class:class_id (
            name,
            level,
            location:location_id (name)
          )
        ),
        absence:absence_id (date, class:class_id (name)),
        created_by_profile:created_by (first_name, last_name, email)
      `;
      const result = await (supabase
        .from("makeup_bookings")
        .select(selectString) as any)
        .eq("organization_id", organizationId)
        .order("created_at", { ascending: false });
      const { data, error } = result as { data: MakeupBookingAdminRow[]; error: any };
      if (error) {
        console.error("Error fetching all makeup bookings:", error);
        throw error;
      }
      return data ?? [];
    } catch (error) {
      console.error("Error in getAllMakeupBookings:", error);
      toast.error("Failed to load makeup bookings");
      return [];
    }
  },

  /**
   * Admin: Book a makeup directly into a class (no absence required)
   * Will create a makeup_class slot if one does not exist for the class/date/time.
   */
  adminBookMakeup: async ({
    student_id,
    class_id,
    organization_id,
    date,
    start_time,
    end_time,
    notes
  }: {
    student_id: string;
    class_id: string;
    organization_id: string;
    date?: string;
    start_time?: string;
    end_time?: string;
    notes?: string;
  }): Promise<any> => {
    try {
      // 1. Find or create a makeup_class slot for this class/date/time
      let makeupClassId: string | null = null;
      if (class_id && date && start_time && end_time) {
        // Try to find existing slot
        const { data: existing, error: findError } = await supabase
          .from('makeup_classes')
          .select('id, current_students, max_students')
          .eq('class_id', class_id)
          .eq('date', date)
          .eq('start_time', start_time)
          .eq('end_time', end_time)
          .eq('organization_id', organization_id)
          .maybeSingle();
        if (findError) throw findError;
        if (existing && existing.current_students < existing.max_students) {
          makeupClassId = existing.id;
        } else {
          // Create a new slot (default max_students = 5)
          const newMakeupClassId = uuidv4();
          const { data: created, error: createError } = await supabase
            .from('makeup_classes')
            .insert({
              id: newMakeupClassId,
              makeup_class_id: newMakeupClassId,
              makeup_date: date,
              original_class_id: class_id,
              original_date: date,
              class_id,
              date,
              start_time,
              end_time,
              max_students: 5,
              current_students: 0,
              organization_id,
              status: 'open',
              student_id: null
            })
            .select('id')
            .single();
          if (createError) throw createError;
          makeupClassId = created.id;
        }
      } else {
        throw new Error('Missing class/date/time for admin makeup booking');
      }
      // 2. Book the student into the makeup_class
      const { data, error } = await supabase
        .from('makeup_bookings')
        .insert({
          student_id,
          makeup_class_id: makeupClassId,
          absence_id: null,
          notes,
          status: 'scheduled',
        })
        .select()
        .single();
      if (error) throw error;
      // Log to activity_feed
      try {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData?.user?.id;
        await supabase.from('activity_feed').insert({
          organization_id,
          user_id: userId,
          type: 'makeup_booking',
          message: `Admin booked makeup for student`,
          data: {
            student_id,
            makeup_class_id: makeupClassId,
            date,
            start_time,
            end_time,
            notes,
            status: 'scheduled',
          },
        });
      } catch (err) {
        // Log but don't block
        console.warn('Failed to log activity_feed for admin makeup booking', err);
      }
      toast.success('Makeup booked successfully');
      return data;
    } catch (error) {
      console.error('Error in adminBookMakeup:', error);
      toast.error('Failed to book makeup');
      throw error;
    }
  },
}; 